import { MONEY_SCALE, ZERO_MONEY, add, isNegative, isPositive, isZero, negate, subtract, } from '@aeris/money';
/**
 * The signed contribution of one movement.
 *
 * `in` adds, `out` subtracts. The amount itself is always positive — migration
 * 0011 has a check constraint saying so — which is why the direction has to be
 * applied here rather than assumed from a sign that is never there.
 */
export const signedAmount = (movement) => movement.direction === 'in' ? movement.amount : negate(movement.amount);
/**
 * What the drawer should hold, derived from the ledger.
 *
 * **This is the function that makes a till a till.** The operator types what
 * they counted; this says what should have been there; the difference between
 * them is the thing worth discovering. Nothing anywhere in this feature accepts
 * an expected amount as input — not the contracts, not the API, not the
 * repository — because a till where somebody can type the expected figure is a
 * till that never has a difference.
 *
 * The opening float is included because it is written to the ledger as an
 * `opening` movement, which is what keeps this a plain sum rather than
 * `openingFloat + sum(...)` — the version somebody eventually forgets half of.
 *
 * The result can be negative. That is a real state (more refunded or withdrawn
 * than ever came in) and an alarming one, and rounding it up to zero would hide
 * exactly what the number is for.
 */
export const expectedAmount = (movements) => {
    let total = ZERO_MONEY;
    for (const movement of movements) {
        total = add(total, signedAmount(movement));
    }
    // Every input is already at MONEY_SCALE, so `add` cannot have grown the
    // scale; this is a statement of the contract rather than a correction.
    return total.scale === MONEY_SCALE
        ? total
        : { units: total.units, scale: total.scale };
};
/**
 * `counted - expected`.
 *
 * Positive is a sobra (more cash in the drawer than the ledger accounts for);
 * negative is a falta. The database computes the stored value as a generated
 * column so the application cannot write a third answer; this function is the
 * same arithmetic for a screen showing the figure before it is committed.
 */
export const differenceOf = (counted, expected) => subtract(counted, expected);
export const differenceKind = (difference) => {
    if (isZero(difference))
        return 'exact';
    return isPositive(difference) ? 'surplus' : 'shortfall';
};
// ---------------------------------------------------------------------------
// Whether a movement may be recorded
// ---------------------------------------------------------------------------
/**
 * Whether a sangria may take the expected balance below zero.
 *
 * **This is the one place this is decided**, and it is a named constant for the
 * same reason `ALLOW_NEGATIVE_STOCK` is: it is a business policy rather than a
 * data-integrity rule, and it will eventually be a per-tenant setting.
 *
 * The distinction that matters, and the one worth stating loudly because the
 * two are easy to confuse:
 *
 *   - refusing a WITHDRAWAL larger than the drawer holds is refusing a typo.
 *     Nobody removes R$ 500 from a drawer containing R$ 100; the operator meant
 *     R$ 50,00 and typed R$ 500,00. Catching it at the keyboard is worth far
 *     more than recording it faithfully.
 *
 *   - refusing a DIFFERENCE at closing would be refusing the truth, and this
 *     feature does the opposite: a shortfall is recorded, never blocked. See
 *     `checkClose`. The point of a till is that a shortfall is *discovered*,
 *     not that it is prevented.
 *
 * `adjustment` deliberately bypasses this, because a correction downward is how
 * somebody records that the drawer really does hold less than the ledger says.
 */
export const ALLOW_NEGATIVE_CASH = false;
/**
 * Applies a movement to a session's expected balance and says whether it is
 * allowed.
 *
 * The status check is first and is absolute: **a closed session takes no
 * further movements.** That is what "closing a session with movements still to
 * come is refused" means in practice — the close takes a row lock on the
 * session and computes the expected amount under it, so a movement racing the
 * close either lands before it (and is counted) or arrives after (and is
 * refused here). There is no ordering in which a movement can slip past the sum
 * that was reconciled against.
 */
export const checkMovement = (session, expected, kind, direction, amount, allowNegative = ALLOW_NEGATIVE_CASH) => {
    if (session.status !== 'open') {
        return { ok: false, refusal: { reason: 'session-closed' } };
    }
    const next = direction === 'in' ? add(expected, amount) : subtract(expected, amount);
    if (!allowNegative && kind !== 'adjustment' && isNegative(next)) {
        return {
            ok: false,
            refusal: {
                reason: 'insufficient-cash',
                available: expected,
                requested: amount,
            },
        };
    }
    return { ok: true, expected: next };
};
/**
 * Who may close this session, and whether it can be closed at all.
 *
 * Note what is NOT here: any check on the difference. Closing with a shortfall
 * must succeed. A till that refuses to close until the cash balances is a till
 * whose operator finds the missing note in their pocket, and the discrepancy is
 * never recorded — which is precisely the failure the fechamento exists to
 * prevent. Section 29 wants the event recorded with who, when and how much; it
 * does not want it prevented.
 *
 * `supervises` is passed in rather than looked up, because this package knows
 * nothing about the permission catalogue and must not: it is imported by the
 * browser, where a permission answer is a hint, and by the server, where it is
 * a decision.
 */
export const checkClose = (session, actorUserId, supervises) => {
    if (session.status !== 'open') {
        return { ok: false, refusal: { reason: 'already-closed' } };
    }
    if (session.openedByUserId !== actorUserId && !supervises) {
        return { ok: false, refusal: { reason: 'not-your-session' } };
    }
    return { ok: true };
};
/**
 * A session that has been closed is closed for good.
 *
 * There is no reopen endpoint, no `status = 'open'` update path in the
 * repository, and this function exists so the refusal has a name a caller can
 * read rather than being the absence of a feature.
 *
 * Reopening is refused because the closing figures — counted, expected,
 * difference — describe a drawer at a moment, and a session that reopens makes
 * all three describe nothing. A shift that continues after a fechamento is a
 * NEW session on the same terminal, which the partial unique index permits the
 * instant the first one closed. That is the operation people actually want, and
 * it leaves both shifts individually reconcilable.
 */
export const canReopen = () => false;
//# sourceMappingURL=rules.js.map